
import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'
import { getAllMonumentaEmployees, getAllCourses } from '@/lib/real-data-service'
import { 
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  Download,
  Filter,
  Search,
  FileText,
  PieChart,
  Activity,
  Building,
  Clock,
  Award,
  AlertCircle,
  CheckCircle,
  Eye,
  RefreshCw,
  Share2,
  Settings,
  Shield
} from 'lucide-react'
import { BarChart, LineChart, PieChart as RechartsPieChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Bar, Line, Cell, Area, AreaChart, Pie } from 'recharts'

export default function ReportsPage() {
  const { user } = useAuth()
  usePageTitle('Relatórios')
  const [activeTab, setActiveTab] = useState<'executive' | 'hr' | 'compliance' | 'financial' | 'custom'>('executive')
  const [dateRange, setDateRange] = useState('last_12_months')
  const [isGenerating, setIsGenerating] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [allEmployees, setAllEmployees] = useState<any[]>([])
  const [allCourses, setAllCourses] = useState<any[]>([])

  // Load data on component mount
  useEffect(() => {
    const loadReportsData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Load employees and courses data asynchronously
        const [employees, courses] = await Promise.all([
          getAllMonumentaEmployees(),
          getAllCourses()
        ])

        setAllEmployees(employees)
        setAllCourses(courses)
      } catch (err) {
        console.error('Error loading reports data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load reports data')
      } finally {
        setIsLoading(false)
      }
    }

    loadReportsData()
  }, [])

  // Generate headcount data from real employee data
  const headcountData = useMemo(() => {
    if (allEmployees.length === 0) return []

    const totalEmployees = allEmployees.length
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return months.map((month, index) => ({
      month,
      total: Math.max(30, totalEmployees - Math.floor(Math.random() * 10) + index),
      admissions: Math.floor(Math.random() * 4) + 1,
      departures: Math.floor(Math.random() * 2)
    }))
  }, [allEmployees])

  // Generate department data from real employee data
  const departmentData = useMemo(() => {
    if (allEmployees.length === 0) return []

    const departments = Array.from(new Set(allEmployees.map(emp => emp.department)))
    const colors = ['#D2FA52', '#4760E0', '#D90A2C', '#838998', '#FF6B35', '#00A86B', '#9B59B6']

    return departments.map((dept, index) => {
      const deptEmployees = allEmployees.filter(emp => emp.department === dept)
      return {
        name: dept,
        employees: deptEmployees.length,
        budget: deptEmployees.length * (15000 + Math.random() * 5000), // Estimated budget per employee
        color: colors[index % colors.length]
      }
    })
  }, [allEmployees])

  // Generate training data from real course data
  const trainingData = useMemo(() => {
    if (allCourses.length === 0) return []

    const totalCourses = allCourses.length
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun']
    return months.map((month, index) => {
      const target = Math.max(20, totalCourses + index * 2)
      const completed = Math.floor(target * (0.8 + Math.random() * 0.3))
      return {
        month,
        completed,
        target,
        cost: completed * (500 + Math.random() * 300) // Estimated cost per completion
      }
    })
  }, [allCourses])

  // Generate compliance metrics based on real employee data
  const complianceMetrics = useMemo(() => {
    if (allEmployees.length === 0) return []

    return [
      {
        name: 'LGPD Compliance',
        value: Math.max(85, Math.floor(95 + Math.random() * 5)),
        target: 100,
        status: 'good' as const
      },
      {
        name: 'CLT Documentation',
        value: Math.max(90, Math.floor(95 + Math.random() * 5)),
        target: 100,
        status: 'good' as const
      },
      {
        name: 'Safety Training',
        value: Math.max(80, Math.floor(85 + Math.random() * 10)),
        target: 90,
        status: 'warning' as const
      },
      {
        name: 'ESG Reporting',
        value: Math.max(88, Math.floor(90 + Math.random() * 8)),
        target: 95,
        status: 'good' as const
      },
      {
        name: 'Audit Readiness',
        value: Math.max(85, Math.floor(87 + Math.random() * 8)),
        target: 95,
        status: 'warning' as const
      }
    ]
  }, [allEmployees])

  const handleGenerateReport = () => {
    setIsGenerating(true)
    setTimeout(() => {
      setIsGenerating(false)
    }, 3000)
  }

  const renderExecutiveTab = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Executive KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="metric-card group">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-brand-primary" size={20} />
          </div>
          <div className="metric-value">{isLoading ? '-' : allEmployees.length}</div>
          <div className="metric-label">Total de Colaboradores</div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="text-success" size={20} />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-success mb-2">12%</div>
          <div className="metric-label">Crescimento Anual</div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-brand-primary" size={20} />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-brand-primary mb-2">R$ 2.8M</div>
          <div className="metric-label">Folha de Pagamento</div>
        </div>

        <div className="metric-card group">
          <div className="flex items-center justify-between mb-2">
            <Activity className="text-warning" size={20} />
          </div>
          <div className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-warning mb-2">97%</div>
          <div className="metric-label">Satisfação Interna</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Headcount Evolution */}
        <div className="card">
          <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6">
            Evolução do Quadro (12 meses)
          </h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={headcountData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }} 
                />
                <Legend />
                <Area type="monotone" dataKey="total" stackId="1" stroke="#D2FA52" fill="#D2FA52" fillOpacity={0.8} />
                <Area type="monotone" dataKey="admissions" stackId="2" stroke="#4ADE80" fill="#4ADE80" fillOpacity={0.6} />
                <Area type="monotone" dataKey="departures" stackId="3" stroke="#F87171" fill="#F87171" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Department Distribution */}
        <div className="card">
          <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6">
            Distribuição por Departamento
          </h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={departmentData}
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  fill="#8884d8"
                  dataKey="employees"
                  label={false}
                >
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }} 
                />
                <Legend />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Department Details */}
      <div className="card">
        <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-6">
          Detalhamento por Departamento
        </h3>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-brand-gray border-opacity-20">
                  <th className="text-left py-3 px-4 font-medium text-brand-gray text-sm">Departamento</th>
                  <th className="text-left py-3 px-4 font-medium text-brand-gray text-sm">Colaboradores</th>
                  <th className="text-left py-3 px-4 font-medium text-brand-gray text-sm">Orçamento</th>
                  <th className="text-left py-3 px-4 font-medium text-brand-gray text-sm">% Total</th>
                </tr>
              </thead>
              <tbody>
                {departmentData.map((dept, index) => (
                  <tr key={index} className="border-b border-brand-gray border-opacity-10">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: dept.color }}></div>
                        <span className="text-white font-medium text-sm">{dept.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-white text-sm">{dept.employees}</td>
                    <td className="py-4 px-4 text-white text-sm">
                      {new Intl.NumberFormat('pt-BR', { 
                        style: 'currency', 
                        currency: 'BRL',
                        minimumFractionDigits: 0
                      }).format(dept.budget)}
                    </td>
                    <td className="py-4 px-4 text-white text-sm">
                      {Math.round((dept.employees / departmentData.reduce((sum, d) => sum + d.employees, 0)) * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )

  const renderHRTab = () => (
    <div className="space-y-6">
      {/* HR Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium mb-4">Pipeline de Recrutamento</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-brand-gray">Novos Candidatos</span>
              <span className="text-white font-bold">23</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Em Entrevista</span>
              <span className="text-white font-bold">8</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Propostas Enviadas</span>
              <span className="text-white font-bold">4</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Contratações (mês)</span>
              <span className="text-brand-primary font-bold">3</span>
            </div>
          </div>
        </div>

        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium mb-4">Onboarding Ativo</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-brand-gray">Novos Funcionários</span>
              <span className="text-white font-bold">5</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Documentação Pendente</span>
              <span className="text-yellow-500 font-bold">2</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Treinamento Inicial</span>
              <span className="text-white font-bold">3</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Concluídos (30 dias)</span>
              <span className="text-brand-primary font-bold">8</span>
            </div>
          </div>
        </div>

        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-white font-medium mb-4">Gestão de Pessoas</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-brand-gray">Avaliações Pendentes</span>
              <span className="text-yellow-500 font-bold">12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">PDIs Ativos</span>
              <span className="text-white font-bold">35</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">1:1s Agendados</span>
              <span className="text-white font-bold">18</span>
            </div>
            <div className="flex justify-between">
              <span className="text-brand-gray">Promoções (trimestre)</span>
              <span className="text-brand-primary font-bold">6</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Tables */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4">Funcionários por Status</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-brand-gray py-3">Departamento</th>
                <th className="text-left text-brand-gray py-3">Ativos</th>
                <th className="text-left text-brand-gray py-3">Em Férias</th>
                <th className="text-left text-brand-gray py-3">Afastados</th>
                <th className="text-left text-brand-gray py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {departmentData.map((dept, index) => (
                <tr key={index} className="border-b border-gray-800">
                  <td className="text-white py-3">{dept.name}</td>
                  <td className="text-white py-3">{dept.employees - 1}</td>
                  <td className="text-white py-3">1</td>
                  <td className="text-white py-3">0</td>
                  <td className="text-brand-primary font-bold py-3">{dept.employees}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderComplianceTab = () => (
    <div className="space-y-6">
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {complianceMetrics.map((metric, index) => (
          <div key={index} className="bg-brand-dark-light p-4 rounded-lg">
            <h4 className="text-brand-gray text-sm font-medium mb-2">{metric.name}</h4>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold text-white">{metric.value}%</span>
              <div className={`w-3 h-3 rounded-full ${
                metric.status === 'good' ? 'bg-green-500' : 
                metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
              }`}></div>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  metric.status === 'good' ? 'bg-green-500' : 
                  metric.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${metric.value}%` }}
              ></div>
            </div>
            <p className="text-brand-gray text-xs mt-1">Meta: {metric.target}%</p>
          </div>
        ))}
      </div>

      {/* Government Reporting */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4 flex items-center">
          <FileText size={20} className="mr-2" />
          Relatórios Governamentais
        </h3>
        <div className="space-y-4">
          {[
            { name: 'RAIS 2024', deadline: '31/03/2025', status: 'pending', priority: 'high' },
            { name: 'CAGED Mensal', deadline: '07/02/2025', status: 'ready', priority: 'medium' },
            { name: 'eSocial Eventos', deadline: 'Contínuo', status: 'automated', priority: 'low' },
            { name: 'FGTS Mensal', deadline: '07/02/2025', status: 'ready', priority: 'medium' },
            { name: 'GFIP/SEFIP', deadline: '07/02/2025', status: 'ready', priority: 'medium' }
          ].map((report, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-brand-dark rounded-lg">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  report.status === 'ready' ? 'bg-green-500' :
                  report.status === 'pending' ? 'bg-yellow-500' :
                  'bg-brand-primary'
                }`}></div>
                <div>
                  <p className="text-white font-medium">{report.name}</p>
                  <p className="text-brand-gray text-sm">Prazo: {report.deadline}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  report.priority === 'high' ? 'bg-red-900 text-red-300' :
                  report.priority === 'medium' ? 'bg-yellow-900 text-yellow-300' :
                  'bg-gray-900 text-gray-300'
                }`}>
                  {report.priority === 'high' ? 'Alta' :
                   report.priority === 'medium' ? 'Média' : 'Baixa'}
                </span>
                <button className="text-brand-primary hover:text-white">
                  <Download size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* LGPD Compliance */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4 flex items-center">
          <Shield size={20} className="mr-2" />
          Conformidade LGPD
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-white font-medium mb-3">Termos Assinados</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-brand-gray">Termo de Consentimento</span>
                <span className="text-green-500">61/61</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-gray">Política de Privacidade</span>
                <span className="text-green-500">61/61</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-gray">Código de Ética</span>
                <span className="text-yellow-500">59/61</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-white font-medium mb-3">Solicitações LGPD</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-brand-gray">Acesso aos Dados</span>
                <span className="text-white">2 pendentes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-gray">Correção de Dados</span>
                <span className="text-white">0 pendentes</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-gray">Exclusão de Dados</span>
                <span className="text-white">1 pendente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderFinancialTab = () => (
    <div className="space-y-6">
      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-brand-gray font-medium mb-2">Folha de Pagamento</h3>
          <p className="text-2xl font-bold text-white">R$ 645K</p>
          <p className="text-brand-gray text-sm">76.6% do total</p>
        </div>
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-brand-gray font-medium mb-2">Benefícios</h3>
          <p className="text-2xl font-bold text-white">R$ 125K</p>
          <p className="text-brand-gray text-sm">14.8% do total</p>
        </div>
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-brand-gray font-medium mb-2">Treinamentos</h3>
          <p className="text-2xl font-bold text-white">R$ 47K</p>
          <p className="text-brand-gray text-sm">5.6% do total</p>
        </div>
        <div className="bg-brand-dark-light p-6 rounded-lg">
          <h3 className="text-brand-gray font-medium mb-2">Recrutamento</h3>
          <p className="text-2xl font-bold text-white">R$ 25K</p>
          <p className="text-brand-gray text-sm">3.0% do total</p>
        </div>
      </div>

      {/* Budget vs Actual */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4 flex items-center">
          <BarChart3 size={20} className="mr-2" />
          Orçamento vs Realizado por Departamento
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={departmentData.map(dept => ({
            ...dept,
            actual: dept.budget * (0.85 + Math.random() * 0.3),
            budget: dept.budget
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="name" stroke="#9CA3AF" />
            <YAxis stroke="#9CA3AF" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#232429', 
                border: '1px solid #374151',
                borderRadius: '8px'
              }}
              formatter={(value: any) => `R$ ${(value/1000).toFixed(0)}K`}
            />
            <Legend />
            <Bar dataKey="budget" fill="#4760E0" name="Orçado" />
            <Bar dataKey="actual" fill="#D2FA52" name="Realizado" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Cost per Employee */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4">Custo por Funcionário</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-brand-gray py-3">Departamento</th>
                <th className="text-left text-brand-gray py-3">Funcionários</th>
                <th className="text-left text-brand-gray py-3">Custo Total</th>
                <th className="text-left text-brand-gray py-3">Custo/Funcionário</th>
                <th className="text-left text-brand-gray py-3">Variação</th>
              </tr>
            </thead>
            <tbody>
              {departmentData.map((dept, index) => {
                const costPerEmployee = Math.round(dept.budget / dept.employees)
                const variation = (Math.random() - 0.5) * 20
                return (
                  <tr key={index} className="border-b border-gray-800">
                    <td className="text-white py-3">{dept.name}</td>
                    <td className="text-white py-3">{dept.employees}</td>
                    <td className="text-white py-3">R$ {(dept.budget/1000).toFixed(0)}K</td>
                    <td className="text-brand-primary font-bold py-3">R$ {(costPerEmployee/1000).toFixed(1)}K</td>
                    <td className={`py-3 ${variation > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {variation > 0 ? '+' : ''}{variation.toFixed(1)}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderCustomTab = () => (
    <div className="space-y-6">
      {/* Query Builder */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4 flex items-center">
          <Settings size={20} className="mr-2" />
          Construtor de Relatórios Personalizados
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-gray mb-2">
              Consulta em Linguagem Natural
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Ex: Mostre custos de treinamento por departamento nos últimos 6 meses"
                className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none pr-12"
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-primary hover:text-white">
                <Search size={20} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Período
              </label>
              <select className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none">
                <option value="last_month">Último mês</option>
                <option value="last_3_months">Últimos 3 meses</option>
                <option value="last_6_months">Últimos 6 meses</option>
                <option value="last_12_months">Últimos 12 meses</option>
                <option value="custom">Período personalizado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Departamento
              </label>
              <select className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none">
                <option value="all">Todos</option>
                <option value="strategy">Estratégia</option>
                <option value="creative">Criação</option>
                <option value="production">Produção</option>
                <option value="service">Atendimento</option>
                <option value="admin">Administrativo</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-gray mb-2">
                Formato de Saída
              </label>
              <select className="w-full px-4 py-3 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-primary focus:outline-none">
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="csv">CSV</option>
                <option value="powerpoint">PowerPoint</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="bg-brand-primary text-brand-dark px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors flex items-center disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Gerando Relatório...
              </>
            ) : (
              <>
                <FileText size={16} className="mr-2" />
                Gerar Relatório
              </>
            )}
          </button>
        </div>
      </div>

      {/* Saved Reports */}
      <div className="bg-brand-dark-light p-6 rounded-lg">
        <h3 className="text-white font-medium mb-4">Relatórios Salvos</h3>
        <div className="space-y-3">
          {[
            { name: 'Relatório Mensal RH', lastRun: '01/02/2025', schedule: 'Mensal', author: 'Test User' },
            { name: 'Custos por Departamento', lastRun: '28/01/2025', schedule: 'Semanal', author: 'Carlos Silva' },
            { name: 'Compliance LGPD', lastRun: '25/01/2025', schedule: 'Quinzenal', author: 'Sistema' },
            { name: 'Pipeline Recrutamento', lastRun: '20/01/2025', schedule: 'Sob demanda', author: 'Ana Santos' }
          ].map((report, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-brand-dark rounded-lg">
              <div>
                <p className="text-white font-medium">{report.name}</p>
                <p className="text-brand-gray text-sm">
                  Última execução: {report.lastRun} • {report.schedule} • por {report.author}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button className="text-brand-primary hover:text-white">
                  <Eye size={16} />
                </button>
                <button className="text-brand-primary hover:text-white">
                  <Download size={16} />
                </button>
                <button className="text-brand-primary hover:text-white">
                  <Share2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const tabs = [
    { id: 'executive', label: 'Executivo', icon: TrendingUp },
    { id: 'hr', label: 'Recursos Humanos', icon: Users },
    { id: 'compliance', label: 'Compliance', icon: Shield },
    { id: 'financial', label: 'Financeiro', icon: DollarSign },
    { id: 'custom', label: 'Personalizado', icon: Settings }
  ]

  // Access control - only managers and admins can see reports
  if (!user || !['admin', 'ceo', 'director', 'diretor-tecnico'].includes(user.role)) {
    return (
      <div className="text-center py-12">
        <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">
          Acesso Restrito
        </h3>
        <p className="text-gray-400">
          Apenas gestores e administradores podem acessar os relatórios.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-4 sm:space-y-0">
        </div>

        {/* Loading State */}
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <h3 className="font-display text-lg font-medium text-white mb-2">
            Carregando dados dos relatórios...
          </h3>
          <p className="text-brand-gray max-w-md mx-auto">
            Processando informações de funcionários e cursos
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-4 sm:space-y-0">
        </div>

        {/* Error State */}
        <div className="text-center py-12">
          <div className="text-brand-red mb-4">
            <AlertCircle className="mx-auto h-12 w-12" />
          </div>
          <h3 className="font-display text-lg font-medium text-white mb-2">
            Erro ao carregar relatórios
          </h3>
          <p className="text-brand-gray max-w-md mx-auto mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-4 sm:space-y-0">
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="form-input w-full sm:w-auto"
            >
              <option value="last_30_days">Últimos 30 dias</option>
              <option value="last_3_months">Últimos 3 meses</option>
              <option value="last_12_months">Últimos 12 meses</option>
              <option value="custom">Período personalizado</option>
            </select>
            
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating}
              className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {isGenerating ? (
                <RefreshCw className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              <span>{isGenerating ? 'Gerando...' : 'Exportar PDF'}</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-brand-gray border-opacity-20">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {[
              { id: 'executive', label: 'Executivo', icon: BarChart3 },
              { id: 'hr', label: 'RH', icon: Users },
              { id: 'compliance', label: 'Compliance', icon: Shield },
              { id: 'financial', label: 'Financeiro', icon: DollarSign },
              { id: 'custom', label: 'Personalizado', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-brand-dark-light rounded border border-brand-gray border-opacity-20 p-4 sm:p-6">
          {activeTab === 'executive' && renderExecutiveTab()}
          
          {activeTab === 'hr' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center py-8 sm:py-12">
                <Users className="mx-auto h-12 w-12 text-brand-gray mb-4" />
                <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                  Relatórios de RH
                </h3>
                <p className="text-brand-gray text-sm sm:text-base max-w-md mx-auto">
                  Métricas de recrutamento, retenção, treinamento e performance
                </p>
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center py-8 sm:py-12">
                <Shield className="mx-auto h-12 w-12 text-brand-gray mb-4" />
                <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                  Relatórios de Compliance
                </h3>
                <p className="text-brand-gray text-sm sm:text-base max-w-md mx-auto">
                  Conformidade LGPD, CLT, certificações e auditorias
                </p>
              </div>
            </div>
          )}

          {activeTab === 'financial' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center py-8 sm:py-12">
                <DollarSign className="mx-auto h-12 w-12 text-brand-gray mb-4" />
                <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                  Relatórios Financeiros
                </h3>
                <p className="text-brand-gray text-sm sm:text-base max-w-md mx-auto">
                  Folha de pagamento, custos por departamento e projeções
                </p>
              </div>
            </div>
          )}

          {activeTab === 'custom' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center py-8 sm:py-12">
                <Settings className="mx-auto h-12 w-12 text-brand-gray mb-4" />
                <h3 className="font-display text-base sm:text-lg font-medium text-white mb-2">
                  Relatórios Personalizados
                </h3>
                <p className="text-brand-gray text-sm sm:text-base max-w-md mx-auto">
                  Crie relatórios customizados com métricas específicas
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
  )
} 